import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import api, { API } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/site/Reveal";

function resolveUrl(item) {
  if (item.storage_path) return `${API}/gallery/file/${item.storage_path}`;
  return item.url;
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const shown = items.filter((i) => filter === "all" || i.type === filter);

  return (
    <div className="pt-32 md:pt-44">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange mb-6">/ The Archive</div>
        <h1 className="font-head font-extrabold uppercase leading-[0.82] tracking-tighter text-[16vw] md:text-[12vw]">
          <MaskedLines lines={[<>Gal<span className="text-brand-orange">lery</span></>]} />
        </h1>

        <div className="flex gap-3 mt-10">
          {["all", "image", "video"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`gallery-filter-${f}`}
              className={`font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors ${
                filter === f ? "bg-brand-ink text-white border-brand-ink" : "border-brand-ink/20 text-brand-ink/60 hover:border-brand-orange"
              }`}
            >
              {f === "all" ? "Everything" : f + "s"}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-12 pb-28 md:pb-40">
        <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
          {shown.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
              <div
                onClick={() => setActive(item)}
                data-testid={`gallery-item-${item.id}`}
                className="group relative overflow-hidden cursor-pointer bg-brand-ink"
              >
                {item.type === "video" ? (
                  <>
                    <video src={resolveUrl(item)} className="w-full object-cover" muted />
                    <div className="absolute inset-0 flex items-center justify-center bg-brand-ink/30">
                      <div className="w-14 h-14 rounded-full bg-brand-orange flex items-center justify-center">
                        <Play size={22} className="text-white ml-1" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img src={resolveUrl(item)} alt={item.caption || "gallery"} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                )}
                {item.caption && (
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-brand-ink/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm">{item.caption}</span>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        {shown.length === 0 && <p className="text-brand-ink/40 font-mono text-sm">No media yet.</p>}
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-brand-ink/90 backdrop-blur flex items-center justify-center p-4"
            onClick={() => setActive(null)}
          >
            <button className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white flex items-center justify-center" data-testid="gallery-close">
              <X size={20} />
            </button>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              {active.type === "video" ? (
                <video src={resolveUrl(active)} controls autoPlay className="max-h-[85vh] w-auto" />
              ) : (
                <img src={resolveUrl(active)} alt={active.caption} className="max-h-[85vh] w-auto object-contain" />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
